import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from "react";

/**
 * Scroll-reveal wrapper.
 *
 * Målen (och varför den ser ut så här):
 * 1. **Aldrig flimra på iOS Safari** – all animation sker via CSS `opacity` +
 *    `translate3d`, inte via React state per frame eller framer-motion
 *    per-scroll layout. Vi promotar till egen kompositor-lager under
 *    animation och tar bort `will-change`/`transform` när animationen är
 *    klar, så systerelement (t.ex. sticky header, reading-progress) inte
 *    lider av sub-pixel-repaints.
 * 2. **Ingen re-render vid scroll** – vi kopplar en enda IntersectionObserver
 *    per element, kör `setState` exakt en gång (när elementet blir synligt)
 *    och kopplar sedan bort. Inga scroll-lyssnare, ingen rAF-loop.
 * 3. **Delas mellan komponenter** – vi återanvänder en singleton-observer per
 *    (rootMargin, threshold) så att en artikel med 50 Reveal-block inte
 *    skapar 50 observers.
 * 4. **Respekterar `prefers-reduced-motion`** – då renderas innehållet
 *    direkt utan animation eller transform.
 * 5. **SSR-säker** – renderar färdigt "visible" på servern så första paint
 *    aldrig blockeras eller hydratiseras till fel state.
 */

type RevealProps = {
  children: ReactNode;
  as?: ElementType;
  className?: string;
  /** Sekunder att vänta innan fade in. Håll <= 0.4 för att undvika kösjuka. */
  delay?: number;
  /** IntersectionObserver rootMargin. Default: trigga strax innan i vy. */
  rootMargin?: string;
  /** IntersectionObserver threshold. */
  threshold?: number;
  style?: CSSProperties;
};

// ————————————————————————————————————————————————————————————————
// Singleton-observers per (rootMargin|threshold) för att inte blåsa upp minnet
// eller trigga onödigt många IO-callbacks på mobil.
// ————————————————————————————————————————————————————————————————

type Cb = (entry: IntersectionObserverEntry) => void;
const observerRegistry = new Map<string, { io: IntersectionObserver; map: WeakMap<Element, Cb> }>();

function getObserver(rootMargin: string, threshold: number) {
  const key = `${rootMargin}|${threshold}`;
  let entry = observerRegistry.get(key);
  if (entry) return entry;
  const map = new WeakMap<Element, Cb>();
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        const cb = map.get(e.target);
        if (cb) cb(e);
      }
    },
    { rootMargin, threshold },
  );
  entry = { io, map };
  observerRegistry.set(key, entry);
  return entry;
}

// ————————————————————————————————————————————————————————————————
// prefers-reduced-motion (SSR-safe, delas mellan alla Reveal-instanser)
// ————————————————————————————————————————————————————————————————

function useReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReduce(mq.matches);
    handler();
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  return reduce;
}

// ————————————————————————————————————————————————————————————————
// Kontext så en förälder kan stänga av reveal (t.ex. i print, i modaler).
// ————————————————————————————————————————————————————————————————

const RevealDisabledContext = createContext(false);
export function RevealDisabled({ children }: { children: ReactNode }) {
  return (
    <RevealDisabledContext.Provider value={true}>{children}</RevealDisabledContext.Provider>
  );
}

export const Reveal = forwardRef<HTMLElement, RevealProps>(function Reveal(
  {
    children,
    as = "div",
    className,
    delay = 0,
    rootMargin = "0px 0px -10% 0px",
    threshold = 0.01,
    style,
  },
  forwardedRef,
) {
  const reduce = useReducedMotion();
  const disabled = useContext(RevealDisabledContext);
  const nodeRef = useRef<HTMLElement | null>(null);
  // SSR + första klientrender ⇒ visa direkt "hidden"-state bara efter mount.
  // Detta hindrar en hydration-mismatch där servern renderar synligt men
  // klienten sedan blinkar tillbaka till hidden. Vi mount:ar alltid som
  // visible om vi inte hunnit sätta upp observern än.
  const [state, setState] = useState<"initial" | "hidden" | "visible">("initial");

  useEffect(() => {
    if (reduce || disabled) {
      setState("visible");
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setState("visible");
      return;
    }
    const el = nodeRef.current;
    if (!el) return;

    // Om elementet redan är i viewport vid mount (t.ex. artikelns första
    // stycken) – hoppa animationen helt och visa direkt. Det tar bort
    // "första skärmen fadar in en gång"-flimret på iOS.
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const alreadyInView = rect.top < viewportH * 0.9 && rect.bottom > 0;
    if (alreadyInView) {
      setState("visible");
      return;
    }

    setState("hidden");
    const { io, map } = getObserver(rootMargin, threshold);
    const cb: Cb = (entry) => {
      if (entry.isIntersecting) {
        setState("visible");
        map.delete(el);
        io.unobserve(el);
      }
    };
    map.set(el, cb);
    io.observe(el);
    return () => {
      map.delete(el);
      io.unobserve(el);
    };
  }, [reduce, disabled, rootMargin, threshold]);

  const Component = as as ElementType;

  // Sätt ref både på lokal och (om given) vidarebefordrad.
  const setRefs = (node: HTMLElement | null) => {
    nodeRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) (forwardedRef as { current: HTMLElement | null }).current = node;
  };

  // Vi transitionar bara opacity + transform. Transformen tas bort när
  // animationen är klar (via onTransitionEnd) så inga kvarvarande
  // kompositor-lager stör sticky/fixed-syskon på iOS Safari.
  const isVisible = state === "visible" || state === "initial";
  const revealStyle: CSSProperties = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "none" : "translate3d(0, 12px, 0)",
    transition:
      state === "initial" || reduce || disabled
        ? "none"
        : `opacity 420ms cubic-bezier(0.2, 0.8, 0.2, 1) ${delay}s, transform 420ms cubic-bezier(0.2, 0.8, 0.2, 1) ${delay}s`,
    willChange: state === "hidden" ? "opacity, transform" : undefined,
    // Undvik iOS Safari "flash of un-composited content" på element som
    // överlappar sticky/fixed lager.
    WebkitBackfaceVisibility: "hidden",
    backfaceVisibility: "hidden",
    ...style,
  };

  return (
    <Component ref={setRefs} className={className} style={revealStyle}>
      {children}
    </Component>
  );
});
